import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
} from '@mui/material';
import { createS3Bucket, updateS3Bucket } from '../api/s3';

const AWS_REGIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
  'eu-west-1',
  'eu-west-2',
  'eu-central-1',
  'ap-northeast-1',
  'ap-southeast-1',
  'ap-southeast-2',
];

function S3BucketForm({ bucket, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    region: 'us-east-1',
    aws_role_arn: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (bucket) {
      setFormData({
        name: bucket.name || '',
        region: bucket.region || 'us-east-1',
        aws_role_arn: bucket.aws_role_arn || '',
        description: bucket.description || '',
      });
    }
  }, [bucket]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Bucket name is required');
      return false;
    }
    // S3 bucket naming rules
    if (!/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(formData.name)) {
      setError('Bucket name must be 3-63 characters, lowercase letters, numbers, hyphens, and periods only');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setError('');
    setLoading(true);

    try {
      if (bucket?.id) {
        await updateS3Bucket(bucket.id, formData);
      } else {
        await createS3Bucket(formData);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save bucket configuration');
    } finally {
      setLoading(false);
    }
  };

  const isEditMode = Boolean(bucket?.id);

  return (
    <>
      <DialogTitle>
        {isEditMode ? 'Edit S3 Bucket' : 'Add S3 Bucket'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Bucket Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              autoFocus
              disabled={isEditMode}
              helperText={isEditMode ? "Bucket name cannot be changed" : "Must be globally unique, lowercase, 3-63 characters"}
            />

            <FormControl fullWidth>
              <InputLabel>AWS Region</InputLabel>
              <Select
                name="region"
                value={formData.region}
                onChange={handleChange}
                label="AWS Region"
                disabled={isEditMode}
              >
                {AWS_REGIONS.map(region => (
                  <MenuItem key={region} value={region}>{region}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="AWS Role ARN"
              name="aws_role_arn"
              value={formData.aws_role_arn}
              onChange={handleChange}
              fullWidth
              placeholder="arn:aws:iam::123456789012:role/S3AccessRole"
              helperText="Optional: IAM role ARN for cross-account access"
            />

            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description for this bucket"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.name}
          >
            {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Create Bucket')}
          </Button>
        </DialogActions>
      </form>
    </>
  );
}

export default S3BucketForm;
